/**
 * Password confirmation dialog for sensitive account changes (same look as
 * the identity-repair dialog). Not window.prompt, which shows the password in
 * clear text.
 *
 * `action(password)` runs on submit. If it throws, its message is shown and
 * the dialog stays open for another try (after `retryAfterMs`, when the
 * error has one: the server refuses until then); otherwise the dialog closes
 * and the promise resolves with the action's result. Cancel resolves with
 * null.
 */

import { escapeHtml } from "../../../../engine/core/validation.js";
import { t } from "../../../../shared/i18n/index.js";

/**
 * @param {{title: string, message: string, submitLabel?: string, busyLabel?: string,
 *          cancelLabel?: string, action: (password: string) => Promise<any>}} options
 * @returns {Promise<any|null>}
 */
export function promptForPassword({ title, message, submitLabel = t("common.confirm"), busyLabel = t("account.password.checking"), cancelLabel = t("common.cancel"), action }) {
  return new Promise((resolve) => {
    const modal = document.createElement("div");
    modal.className = "unlock-modal";
    modal.dataset.passwordPrompt = "";
    modal.innerHTML = `
      <div class="unlock-modal-content" role="dialog" aria-modal="true">
        <button class="unlock-close-btn" data-action="cancel" title="${escapeHtml(t("common.cancel"))}" aria-label="${escapeHtml(t("common.cancel"))}">&times;</button>
        <h2>${escapeHtml(title)}</h2>
        <p>${escapeHtml(message)}</p>
        <div class="unlock-error hidden" data-ref="error"></div>
        <form class="unlock-form" data-ref="form">
          <input type="password" class="auth-input" data-ref="password" autocomplete="current-password"
                 placeholder="${escapeHtml(t("account.password.placeholder"))}" required />
          <div style="display:flex; gap:8px;">
            <button type="button" class="auth-btn" data-action="cancel" style="background:#6b7280;">${escapeHtml(cancelLabel)}</button>
            <button type="submit" class="auth-btn" style="flex:1;">${escapeHtml(submitLabel)}</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(modal);

    const ref = (name) => modal.querySelector(`[data-ref="${name}"]`);
    let busy = false;
    const finish = (value) => {
      modal.remove();
      resolve(value);
    };
    const cancel = () => {
      if (!busy) finish(null);
    };
    modal.querySelectorAll('[data-action="cancel"]').forEach((btn) => btn.addEventListener("click", cancel));
    modal.addEventListener("keydown", (e) => {
      if (e.key === "Escape") cancel();
    });

    ref("form").addEventListener("submit", async (e) => {
      e.preventDefault();
      if (busy) return;
      const input = ref("password");
      const password = input.value;
      input.value = "";
      const errorEl = ref("error");
      const submit = e.target.querySelector('button[type="submit"]');
      busy = true;
      submit.disabled = true;
      submit.textContent = busyLabel;
      errorEl.classList.add("hidden");
      try {
        finish(await action(password));
      } catch (err) {
        busy = false;
        errorEl.textContent = err?.message || String(err);
        errorEl.classList.remove("hidden");
        submit.textContent = submitLabel;
        // Refused until then (too many attempts): the button waits too.
        const waitMs = Number(err?.retryAfterMs) || 0;
        submit.disabled = waitMs > 0;
        if (waitMs > 0) setTimeout(() => { if (modal.isConnected) submit.disabled = false; }, waitMs);
        input.focus();
      }
    });
    ref("password").focus();
  });
}
